'use client';
import Link from 'next/link';
import { IRepository } from '@/types';
import { TComparison } from '@/lib/tabularCompareApi';
import { Stack, Card, Row } from '@/ui/primatives';
import CardGrid from '@/ui/CardGrid';
import DebugJson from '@/ui/DebugJson';
import * as Icon from '@/ui/Icon';
import Button from '@/ui/Button';
import * as Styled from './styled';
import { toTimeAgo } from '@/lib/dateTime';
import useOxenSession from '@/hooks/useOxenSession';

import { deleteCompareItem } from './deleteCompareAction';

const CompareCard: React.FC<{ compare: TComparison; repository: IRepository }> = ({ compare, repository }) => {
  const { currentUser, authorization } = useOxenSession();

  const canDeleteCompare =
    authorization.forRepository(repository).canManage() || compare.created_by.username === currentUser?.username;

  const repositoryPath = `/${repository.namespace}/${repository.name}`;
  const {
    created_by,
    definition,
    definition: { data_frames: dataFrames },
    compare_number,
  } = compare;

  const onDeleteClick = async () => {
    if (window.confirm('Are you sure you wish to delete this comparison?')) {
      deleteCompareItem(compare_number.toString(), repository.namespace, repository.name);
    } else {
      console.log('Phew!');
    }
  };

  return (
    <Card stretch shadow>
      <Stack gap={5}>
        <Row gap={40} justifyContent="space-between" alignItems="center">
          <Link className="link" key={compare_number} href={`${repositoryPath}/compare/${compare_number}`}>
            <h6>{definition.title || `Untitled compare #${compare_number}`}</h6>
          </Link>
          <Row gap={10} justifyContent="flex-end" alignItems="center">
            {/* <Button.Secondary small href={`${repositoryPath}/compare/${compare_number}`}>
              Edit
            </Button.Secondary> */}
            <Button.Basic small href={`${repositoryPath}/compare/${compare_number}`}>
              View
            </Button.Basic>
          </Row>
        </Row>
        <div>{definition.description}</div>
        <hr />
        <Row gap={5}>
          <div>
            <div>{dataFrames.left?.path}</div>
            <div>
              <Icon.Branch height={12} /> {dataFrames.left?.version}
            </div>
          </div>
          <Icon.ArrowRight />
          <div>
            <div>{dataFrames.right?.path}</div>
            <div>
              <Icon.Branch height={12} /> {dataFrames.right?.version}
            </div>
          </div>
        </Row>
      </Stack>
      <Row justifyContent="space-between" alignItems="flex-end">
        {canDeleteCompare ? (
          <Button.Secondary small className="danger" onClick={onDeleteClick}>
            Delete
          </Button.Secondary>
        ) : (
          <div />
        )}
        <div>
          Created by: {created_by?.username} {toTimeAgo(compare.inserted_at)}
        </div>
      </Row>
    </Card>
  );
};

const CompareList: React.FC<{
  repository: IRepository;
  comparisons: TComparison[];
  children?: React.ReactNode;
}> = ({ repository, comparisons, children }) => {
  return (
    comparisons?.length > 0 && (
      <Styled.CompareList>
        {children}

        <DebugJson rootNodeName="compareList" json={comparisons} collapseDepth={3} />
        <CardGrid cols={2}>
          {comparisons?.map((compare) => (
            <CompareCard key={compare.compare_number} compare={compare} repository={repository} />
          ))}
        </CardGrid>
      </Styled.CompareList>
    )
  );
};

export default CompareList;
